import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    ArrowRight,
    CheckCircle,
    FileText,
    Settings,
    Rocket,
    HeadphonesIcon,
    Phone,
    Video,
    Smartphone,
    DoorOpen,
    Lock,
    Eye,
    BarChart3,
    Target,
    Workflow,
    Gauge,
} from 'lucide-react';

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
};

const SolutionsPage = () => {
    const methodology = [
        {
            step: '01',
            title: 'Requirements Analysis & Consultation',
            description: 'We begin by understanding your business objectives, current infrastructure, and future needs through comprehensive consultation.',
            icon: FileText,
        },
        {
            step: '02',
            title: 'Systems Design & Solution Modelling',
            description: 'Our experts design tailored solutions using industry-leading standards and best practices.',
            icon: Settings,
        },
        {
            step: '03',
            title: 'Deployment, Optimization & Integration',
            description: 'Seamless implementation with minimal disruption, followed by optimization for peak performance.',
            icon: Rocket,
        },
        {
            step: '04',
            title: 'Handover, Documentation & Continuous Support',
            description: 'Complete knowledge transfer with comprehensive documentation and ongoing support services.',
            icon: HeadphonesIcon,
        },
    ];

    const standardsCategories = [
        {
            title: 'Structured Cabling & Physical Infrastructure',
            description: 'These standards ensure consistent performance, interoperability, and long-term, future-proof cabling installation.',
            standards: [
                'ANSI/TIA-568 (Generic Telecommunications Cabling for Customer Premises): The "gold standard" in North America for designing, installing, and testing copper and fiber cabling.',
                'ISO/IEC 11801 (International Standard for Generic Cabling): A worldwide standard that defines requirements for structured cabling in office buildings, industrial premises, and data centers.',
                'ANSI/TIA-569 (Telecommunications Pathways and Spaces): Defines the standards for conduits, cable trays, and telecommunications rooms to ensure proper cable management.',
                'ANSI/TIA-606 (Administration Standard): Covers the labeling, documentation, and record-keeping required to manage the infrastructure.',
                'ANSI/TIA-607 (Grounding and Bonding Standard): Ensures proper electrical grounding for safety and signal integrity.',
                'BS EN 50173 / BS EN 50174 (European Standards): Specifically for European projects, detailing the planning and installation of cabling systems.',
            ],
        },
        {
            title: 'Data Center Infrastructure',
            description: 'These standards focus on the design, physical security, and environmental controls of data centers.',
            standards: [
                'ANSI/TIA-942 (Data Center Telecommunications Infrastructure Standard): Provides comprehensive guidelines for data center layout, including cabling, networking, and security to ensure reliability.',
                'ISO/IEC 24764 (Data Center Cabling Standard): International standard for cabling specifically within data centers.',
                'Uptime Institute Tier Standards (I-IV): Defines the data center facility\'s performance, uptime, and redundancy (e.g., Tier III for 99.982% availability).',
            ],
        },
        {
            title: 'Network Performance & Communication (IEEE)',
            description: 'These standards define how devices communicate over the physical infrastructure.',
            standards: [
                'IEEE 802.3 (Ethernet Standard): Defines speeds and media types, including PoE (Power over Ethernet) and high-speed data transmission (10GBASE-T, 40G, 100G).',
                'IEEE 802.11 (Wi-Fi Standards): Specific to Wi-Fi 6/7 wireless network performance.',
            ],
        },
        {
            title: 'Security & Governance Frameworks',
            description: 'Essential for protecting data, managing risk, and meeting regulatory requirements.',
            standards: [
                'Zero Trust Architecture (ZTA): A modern approach focusing on continuous verification, identity-aware access, and micro-segmentation rather than relying on perimeter security.',
                'NIST Cybersecurity Framework (CSF 2.0): A widely adopted risk-based framework covering Govern, Identify, Protect, Detect, Respond, and Recover functions.',
                'ISO/IEC 27001 (Information Security Management System): Defines requirements for managing security, cloud-native infrastructure, and supply chain security.',
                'PCI DSS (Payment Card Industry Data Security Standard): Mandatory for infrastructures handling credit card information.',
                'DORA (Digital Operational Resilience Act): Crucial for financial IT providers operating in the EU, effective 2026, focusing on operational resilience and risk monitoring.',
            ],
        },
        {
            title: 'Specialized Industrial Standards',
            description: '',
            standards: [
                'ANSI/TIA-1005-B (Industrial Premises): Telecommunications infrastructure requirements for industrial environments.',
                'ISA/IEC 62443: Standards specifically for the security of Industrial Automation and Control Systems (IACS).',
            ],
        },
    ];

    const voipSolutions = [
        { icon: Phone, title: 'IP PBX', description: 'Appliances & Soft Switches' },
        { icon: Phone, title: 'VoIP Telephony', description: 'Modern IP-based voice communication' },
        { icon: Smartphone, title: 'Fixed-Mobile Convergence', description: 'Using GSM Gateways' },
        { icon: Video, title: 'Video Communication', description: 'Telepresence solutions' },
        { icon: DoorOpen, title: 'IP Video Door Systems', description: 'Entry & intercom solutions' },
        { icon: Lock, title: 'Door Inter-lock Solutions', description: 'Secure access management' },
        { icon: Eye, title: 'IP Surveillance', description: 'Network-based monitoring' },
    ];

    const alignmentFramework = [
        { step: 'PLAN', description: 'Clear identification of business priorities', icon: Target },
        { step: 'MODEL', description: 'Mapping business services to IT services', icon: Workflow },
        { step: 'MANAGE', description: 'Effective service management across lifecycle', icon: Settings },
        { step: 'MEASURE', description: 'Continuous feedback and improvement', icon: Gauge },
    ];

    return (
        <div data-testid="solutions-page" className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-44 pb-24 bg-slate-900 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                        alt="Data center"
                        className="w-full h-full object-cover opacity-20"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-full flex items-center justify-center">
                    <motion.div
                        initial="initial"
                        animate="animate"
                        variants={{
                            animate: { transition: { staggerChildren: 0.1 } },
                        }}
                        className="max-w-3xl text-center"
                    >
                        <motion.span
                            variants={fadeUp}
                            className="inline-block font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-6"
                        >
                            Technology Solutions
                        </motion.span>

                        <motion.h1
                            variants={fadeUp}
                            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
                            data-testid="solutions-hero-title"
                        >
                            Service Delivery
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="text-lg text-slate-300 leading-relaxed"
                        >
                            Standards-driven methodologies.
                        </motion.p>
                    </motion.div>
                </div>
            </section>

            {/* Network Infrastructure Methodology */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="max-w-3xl mb-16"
                    >
                        <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-500 mb-4 block">
                            Network Infrastructure Solutions
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                            Our Methodology
                        </h2>
                        <p className="text-slate-600 leading-relaxed">
                            To deliver resilient, scalable, and high-performance network systems,
                            Vanfrank adopts a standards-driven, consultative methodology.
                        </p>
                    </motion.div>

                    {/* Methodology Steps - Tracing Beam Style */}
                    <div data-testid="methodology-steps" className="relative">
                        {/* Vertical Line */}
                        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-blue-500 to-slate-200 hidden md:block"></div>

                        <div className="space-y-8">
                            {methodology.map((item, index) => (
                                <motion.div
                                    key={item.step}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.15 }}
                                    className="relative md:pl-24"
                                >
                                    {/* Step Number Circle */}
                                    <div className="absolute left-0 top-0 w-16 h-16 rounded-full bg-blue-500 text-white font-mono font-bold text-lg flex items-center justify-center shadow-[0_0_30px_rgba(59,130,246,0.4)] hidden md:flex">
                                        {item.step}
                                    </div>

                                    <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-lg hover:border-blue-500/20 transition-all duration-300">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0 md:hidden">
                                                <span className="font-mono font-bold text-blue-500">{item.step}</span>
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-slate-900 mb-3">
                                                    {item.title}
                                                </h3>
                                                <p className="text-slate-600 leading-relaxed">
                                                    {item.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Compliance Standards */}
            <section className="py-24 md:py-32 bg-slate-50 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 gap-16">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="lg:col-span-2"
                        >
                            <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-500 mb-4 block">
                                Industry Standards
                            </span>
                            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                                Compliance Standards
                            </h2>

                            <div data-testid="compliance-standards" className="space-y-10">
                                {standardsCategories.map((category, categoryIndex) => (
                                    <motion.div
                                        key={categoryIndex}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: categoryIndex * 0.1 }}
                                        className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm"
                                    >
                                        <h3 className="text-xl font-bold text-slate-900 mb-2">{category.title}</h3>
                                        {category.description && (
                                            <p className="text-slate-600 mb-4 text-sm">{category.description}</p>
                                        )}
                                        <div className="space-y-3">
                                            {category.standards.map((standard, index) => (
                                                <div key={index} className="flex items-start gap-3">
                                                    <CheckCircle className="text-blue-500 flex-shrink-0 mt-1" size={18} />
                                                    <span className="text-slate-700 text-sm leading-relaxed">{standard}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="mt-8 p-6 bg-blue-50 rounded-xl text-slate-700 leading-relaxed border border-blue-100"
                            >
                                By adhering to these, we ensure our network infrastructure implementations are not just functional, 
                                but reliable, secure, and ready for future advancements like AI and high-density computing.
                            </motion.p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* VoIP & SIP Trunking */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-500 mb-4 block">
                            Communication Solutions
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                            Voice Over IP & SIP Trunking
                        </h2>
                        <p className="text-slate-600 max-w-2xl mx-auto">
                            Vanfrank delivers next-generation IP Telephony and SIP Trunking Solutions
                            that help enterprises modernize their communication systems.
                        </p>
                    </motion.div>

                    <div data-testid="voip-solutions" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {voipSolutions.map((solution, index) => (
                            <motion.div
                                key={solution.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-blue-500/30 hover:shadow-lg transition-all duration-300 group"
                            >
                                <div className="w-12 h-12 rounded-xl bg-slate-100 group-hover:bg-blue-500 flex items-center justify-center mb-4 transition-colors duration-300">
                                    <solution.icon
                                        className="text-slate-600 group-hover:text-white transition-colors duration-300"
                                        size={22}
                                    />
                                </div>
                                <h3 className="font-bold text-slate-900 mb-2">{solution.title}</h3>
                                <p className="text-slate-600 text-sm">{solution.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Business-IT Alignment */}
            <section className="py-24 md:py-32 bg-slate-900 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-4 block">
                            Strategic Alignment
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                            Business-IT Alignment Framework
                        </h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            We deliver technology solutions that directly support business strategy,
                            ensuring clear identification of priorities and effective service management.
                        </p>
                    </motion.div>

                    <div data-testid="alignment-framework" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {alignmentFramework.map((item, index) => (
                            <motion.div
                                key={item.step}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="relative"
                            >
                                <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-blue-500/30 transition-all duration-300 h-full">
                                    <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center mb-6">
                                        <item.icon className="text-blue-400" size={24} />
                                    </div>
                                    <div className="font-mono text-2xl font-bold text-blue-400 mb-3">
                                        {item.step}
                                    </div>
                                    <p className="text-slate-300 leading-relaxed">{item.description}</p>
                                </div>
                                {/* Arrow connector */}
                                {index < 3 && (
                                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-blue-500">
                                        <ArrowRight size={24} />
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                            Ready to Transform Your Infrastructure?
                        </h2>
                        <p className="text-slate-600 text-lg mb-10 max-w-2xl mx-auto">
                            Let's discuss how our enterprise solutions can help you achieve
                            operational excellence and business growth.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="solutions-contact-cta"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-full font-semibold hover:bg-slate-800 transition-colors shadow-[0_0_30px_rgba(15,23,42,0.3)]"
                            >
                                Get Started
                                <ArrowRight size={18} />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="solutions-services-cta"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-slate-900 border border-slate-200 rounded-full font-semibold hover:bg-slate-50 transition-colors"
                            >
                                View All Services
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default SolutionsPage;
